import { audio } from '../../../../../audio/AudioManager.js';
import { SaveManager } from '../../../../../state/SaveManager.js';
import { applicationLifecycle } from '../../../../shared/lifecycle/index.js';
import { NUMBER_KINGDOM_ASSETS } from '../../NumberKingdomAssets.js';
import {
  NUMBER_KINGDOM_DISTRICTS,
  getNumberKingdomState,
  visitNumberKingdomDistrict,
} from '../../NumberKingdomState.js';
import {
  configureNumberKingdomDistrictRenderer,
  getNumberKingdomDistrictLayout,
} from '../../components/NumberKingdomDistrictLayout.js';
import {
  createNumberKingdomHomeButton,
  type NumberKingdomHomeButton,
} from '../../components/NumberKingdomHomeButton.js';
import {
  ADDITION_CITADEL_ASSETS,
  ADDITION_CITADEL_IMAGE_ASSETS,
  getMissingAdditionCitadelImages,
} from './AdditionCitadelAssets.js';
import {
  completeAdditionCitadel,
  discoverAdditionCitadel,
  discoverNumberForge,
  getAdditionCitadelState,
} from './AdditionCitadelState.js';
import { TwinTowerCombineView } from './minigames/TwinTowerCombineView.js';
import { ADDITION_CITADEL_MAKE_TEN_1 } from './minigames/TwinTowerCombineConfig.js';
import type { TwinTowerCombineSnapshot } from './minigames/TwinTowerCombineState.js';
import { canEnterNumberForge } from './number-forge/NumberForgeAssets.js';
import { NUMBER_FORGE_SCENE_KEY } from './number-forge/NumberForgeScene.js';

export const ADDITION_CITADEL_SCENE_KEY = 'AdditionCitadel';

const FONT_UI = '"Nunito", "Segoe UI", Arial, sans-serif';

type CitadelPhase = 'arrival' | 'challenge' | 'powered';

export class AdditionCitadelScene extends Phaser.Scene {
  static readonly requiredImageKeys = ADDITION_CITADEL_IMAGE_ASSETS
    .filter(asset => asset.required)
    .map(asset => asset.key);

  private background!: Phaser.GameObjects.Image;
  private backLabel!: Phaser.GameObjects.Text;
  private homeButton!: NumberKingdomHomeButton;
  private arrivalCosmo?: Phaser.GameObjects.Image;
  private arrivalPanel?: Phaser.GameObjects.Image;
  private arrivalTitle?: Phaser.GameObjects.Text;
  private arrivalPrompt?: Phaser.GameObjects.Text;
  private enterIcon?: Phaser.GameObjects.Image;
  private enterZone?: Phaser.GameObjects.Zone;
  private poweredGate?: Phaser.GameObjects.Image;
  private poweredTitle?: Phaser.GameObjects.Text;
  private poweredEquation?: Phaser.GameObjects.Text;
  private poweredReplayHint?: Phaser.GameObjects.Text;
  private returnIcon?: Phaser.GameObjects.Image;
  private returnLabel?: Phaser.GameObjects.Text;
  private returnZone?: Phaser.GameObjects.Zone;
  private combineView?: TwinTowerCombineView;
  private phase: CitadelPhase = 'arrival';
  private reducedMotion = false;
  private transitioning = false;
  private unsubscribeLifecycle?: () => void;
  private resizeTimer?: number;

  constructor() {
    super({ key: ADDITION_CITADEL_SCENE_KEY });
  }

  getProductionReadiness() {
    const missingAssets = getMissingAdditionCitadelImages(this.textures);
    return {
      ready: missingAssets.length === 0,
      missingAssets,
    } as const;
  }

  create() {
    const worldState = getNumberKingdomState();
    if (!worldState.bridgeCrossingUnlocked
      || !worldState.districtUnlocks.includes(NUMBER_KINGDOM_DISTRICTS.additionCitadel)) {
      this.scene.start('NumberKingdom');
      return;
    }

    const readiness = this.getProductionReadiness();
    if (!readiness.ready) {
      console.error('[AdditionCitadel] Required production textures are missing:',
        readiness.missingAssets.map(asset => `${asset.key} (${asset.path})`));
      this.scene.start('KingdomCrossroads');
      return;
    }

    configureNumberKingdomDistrictRenderer(this);
    void audio.enterSoundscape('number-kingdom');
    this.reducedMotion = SaveManager.getSettings().reducedMotion
      || (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false);
    this.transitioning = false;
    discoverAdditionCitadel();
    visitNumberKingdomDistrict(NUMBER_KINGDOM_DISTRICTS.additionCitadel);

    this.background = this.add.image(
      640,
      360,
      ADDITION_CITADEL_ASSETS.arrivalBackground.key,
    ).setCrop(0, 0, 1920, 1030).setOrigin(0.5, 515 / 1080)
      .setScale(1280 / 1920, 720 / 1030).setDepth(0);

    this.backLabel = this.add.text(20, 20, '←  Kingdom Crossroads', {
      fontFamily: FONT_UI,
      fontSize: '18px',
      fontStyle: 'bold',
      color: '#ffffff',
      stroke: '#35104d',
      strokeThickness: 6,
    }).setDepth(100).setInteractive({ useHandCursor: true });
    this.backLabel.on('pointerdown', this.returnToCrossroads, this);
    this.homeButton = createNumberKingdomHomeButton(this, this.returnHome);

    this.input.keyboard?.on('keydown-ESC', this.returnToCrossroads, this);
    this.input.keyboard?.on('keydown-M', this.returnHome, this);
    window.addEventListener('resize', this.handleResize);
    this.unsubscribeLifecycle = applicationLifecycle.subscribe({
      onSuspend: () => this.combineView?.setSuspended(true),
      onResume: () => this.combineView?.setSuspended(false),
    });
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, this.shutdown, this);

    if (getAdditionCitadelState().completed) this.showPoweredState(false);
    else this.showArrival();
    this.layout();
  }

  private showArrival() {
    this.phase = 'arrival';
    this.background.clearTint();
    this.arrivalCosmo = this.add.image(
      300,
      550,
      NUMBER_KINGDOM_ASSETS.numberKingdomCosmo.key,
    ).setDepth(20).setDisplaySize(185, 165);
    this.arrivalPanel = this.add.image(
      640,
      112,
      ADDITION_CITADEL_ASSETS.missionPanel.key,
    ).setCrop(145, 145, 750, 215).setOrigin(520 / 900, 252.5 / 363)
      .setDepth(30).setScale(850 / 750, 132 / 215);
    this.arrivalTitle = this.add.text(640, 78, 'ADDITION CITADEL', {
      fontFamily: FONT_UI,
      fontSize: '35px',
      fontStyle: 'bold',
      color: '#fff1a8',
      stroke: '#3a1256',
      strokeThickness: 7,
      shadow: { offsetX: 0, offsetY: 3, blur: 5, color: '#240633', fill: true },
    }).setOrigin(0.5).setDepth(40);
    this.arrivalPrompt = this.add.text(640, 127,
      'The Twin Towers have lost their balance. Enter the courtyard and help their energy combine!', {
        fontFamily: FONT_UI,
        fontSize: '19px',
        fontStyle: 'bold',
        align: 'center',
        color: '#ffffff',
        stroke: '#3a1256',
        strokeThickness: 5,
        wordWrap: { width: 720 },
      }).setOrigin(0.5).setDepth(40);
    this.enterIcon = this.add.image(
      755,
      555,
      ADDITION_CITADEL_ASSETS.answerButton.key,
    ).setCrop(300, 320, 590, 430).setOrigin(595 / 900, 535 / 766)
      .setScale(92 / 590, 78 / 430).setDepth(42);
    this.arrivalPrompt.setInteractive({ useHandCursor: true });
    this.enterIcon.setInteractive({ useHandCursor: true });
    this.enterZone = this.add.zone(640, 420, 390, 400)
      .setDepth(41).setInteractive({ useHandCursor: true });
    [this.arrivalPrompt, this.enterIcon, this.enterZone]
      .forEach(object => object.on('pointerdown', this.startChallenge, this));
    this.input.keyboard?.on('keydown-ENTER', this.startChallenge, this);
    this.input.keyboard?.on('keydown-SPACE', this.startChallenge, this);
  }

  private readonly startChallenge = () => {
    if (this.phase !== 'arrival' || this.transitioning) return;
    this.transitioning = true;
    audio.click();
    this.input.keyboard?.off('keydown-ENTER', this.startChallenge, this);
    this.input.keyboard?.off('keydown-SPACE', this.startChallenge, this);
    this.arrivalCosmo?.destroy();
    this.arrivalPanel?.destroy();
    this.arrivalTitle?.destroy();
    this.arrivalPrompt?.destroy();
    this.enterIcon?.destroy();
    this.enterZone?.destroy();
    this.arrivalCosmo = undefined;
    this.arrivalPanel = undefined;
    this.arrivalTitle = undefined;
    this.arrivalPrompt = undefined;
    this.enterIcon = undefined;
    this.enterZone = undefined;
    this.createChallenge();
    this.transitioning = false;
  };

  private createChallenge() {
    this.background.setTint(0xc4b8d8);
    this.phase = 'challenge';
    this.combineView = new TwinTowerCombineView(this, ADDITION_CITADEL_MAKE_TEN_1, {
      reducedMotion: this.reducedMotion,
      onComplete: this.handleChallengeComplete,
    });
    this.layout();
  }

  private readonly handleChallengeComplete = (snapshot: TwinTowerCombineSnapshot) => {
    if (this.phase !== 'challenge' || this.transitioning) return;
    this.transitioning = true;
    this.combineView?.setSuspended(true);
    const wasCompleted = getAdditionCitadelState().completed;
    if (!wasCompleted) {
      SaveManager.recordAnswer(ADDITION_CITADEL_MAKE_TEN_1.skillId, true);
      completeAdditionCitadel();
    }
    audio.correct();

    const { safe } = getNumberKingdomDistrictLayout(this);
    const sparkle = this.add.image(
      safe.centerX,
      safe.centerY,
      ADDITION_CITADEL_ASSETS.correctSparkle.key,
    ).setCrop(300, 55, 310, 640).setOrigin(455 / 614, 375 / 700)
      .setScale(420 / 310, 530 / 640).setBlendMode(Phaser.BlendModes.ADD)
      .setDepth(120).setAlpha(0);
    const gateEnergy = this.add.image(
      safe.centerX,
      safe.centerY,
      ADDITION_CITADEL_ASSETS.gateBuild.key,
    ).setCrop(0, 120, 690, 580).setOrigin(0.5, 410 / 700)
      .setScale(620 / 690, 520 / 580).setBlendMode(Phaser.BlendModes.ADD)
      .setDepth(119).setAlpha(0);

    const finish = () => {
      sparkle.destroy();
      gateEnergy.destroy();
      this.combineView?.destroy();
      this.combineView = undefined;
      this.showPoweredState(true, snapshot);
      this.transitioning = false;
    };

    if (this.reducedMotion) {
      sparkle.setAlpha(0.75);
      gateEnergy.setAlpha(0.45);
      this.time.delayedCall(400, finish);
      return;
    }

    this.tweens.add({
      targets: [sparkle, gateEnergy],
      alpha: { from: 0, to: 0.9 },
      scaleX: '+=0.12',
      scaleY: '+=0.12',
      duration: 760,
      yoyo: true,
      hold: 260,
      ease: 'Sine.easeInOut',
      onComplete: finish,
    });
  };

  private showPoweredState(celebrate: boolean, snapshot?: TwinTowerCombineSnapshot) {
    this.phase = 'powered';
    this.background.clearTint();
    this.poweredGate = this.add.image(
      640,
      400,
      ADDITION_CITADEL_ASSETS.gateOpen.key,
    ).setCrop(20, 70, 970, 890).setOrigin(505 / 1024, 515 / 1024)
      .setScale(560 / 970, 510 / 890).setDepth(25)
      .setAlpha(celebrate && !this.reducedMotion ? 0 : 1);
    this.poweredTitle = this.add.text(640, 82, 'THE NUMBER FORGE IS OPEN!', {
      fontFamily: FONT_UI,
      fontSize: '38px',
      fontStyle: 'bold',
      color: '#fff0a4',
      stroke: '#40145f',
      strokeThickness: 8,
      shadow: { offsetX: 0, offsetY: 4, blur: 7, color: '#22022e', fill: true },
    }).setOrigin(0.5).setDepth(50);
    this.poweredEquation = this.add.text(640, 138,
      `${snapshot?.recomposedAddends[0] ?? 10} + ${snapshot?.recomposedAddends[1] ?? 3} = ${snapshot?.total ?? 13}`,
      {
        fontFamily: FONT_UI,
        fontSize: '32px',
        fontStyle: 'bold',
        color: '#ffffff',
        stroke: '#40145f',
        strokeThickness: 7,
      }).setOrigin(0.5).setDepth(50);
    this.poweredReplayHint = this.add.text(640, 182, 'Tap the gate or press Enter to enter • R replays Twin Towers', {
      fontFamily: FONT_UI,
      fontSize: '17px',
      fontStyle: 'bold',
      color: '#fff8d8',
      stroke: '#40145f',
      strokeThickness: 5,
    }).setOrigin(0.5).setDepth(50);
    this.returnIcon = this.add.image(
      840,
      640,
      ADDITION_CITADEL_ASSETS.answerButton.key,
    ).setCrop(300, 320, 590, 430).setOrigin(595 / 900, 535 / 766)
      .setScale(82 / 590, 70 / 430).setDepth(52)
      .setInteractive({ useHandCursor: true });
    this.returnLabel = this.add.text(785, 640, 'EXPLORE AGAIN', {
      fontFamily: FONT_UI,
      fontSize: '20px',
      fontStyle: 'bold',
      color: '#fff8d8',
      stroke: '#34104e',
      strokeThickness: 6,
    }).setOrigin(1, 0.5).setDepth(52).setInteractive({ useHandCursor: true });
    this.returnZone = this.add.zone(760, 640, 330, 80)
      .setDepth(51).setInteractive({ useHandCursor: true });
    [this.returnIcon, this.returnLabel, this.returnZone]
      .forEach(object => object.on('pointerdown', this.returnToCrossroads, this));
    this.poweredGate.setInteractive({ useHandCursor: true });
    this.poweredGate.on('pointerdown', this.enterNumberForge, this);
    this.input.keyboard?.on('keydown-ENTER', this.enterNumberForge, this);
    this.input.keyboard?.on('keydown-SPACE', this.enterNumberForge, this);
    this.input.keyboard?.on('keydown-R', this.replayChallenge, this);

    if (celebrate) {
      audio.unlock();
      if (!this.reducedMotion) {
        this.tweens.add({
          targets: this.poweredGate,
          alpha: 1,
          duration: 520,
          ease: 'Sine.easeOut',
        });
      }
    }
    this.layout();
  }

  private readonly replayChallenge = () => {
    if (this.phase !== 'powered' || this.transitioning) return;
    this.transitioning = true;
    audio.click();
    this.input.keyboard?.off('keydown-ENTER', this.enterNumberForge, this);
    this.input.keyboard?.off('keydown-SPACE', this.enterNumberForge, this);
    this.input.keyboard?.off('keydown-R', this.replayChallenge, this);
    this.poweredGate?.destroy();
    this.poweredTitle?.destroy();
    this.poweredEquation?.destroy();
    this.poweredReplayHint?.destroy();
    this.returnIcon?.destroy();
    this.returnLabel?.destroy();
    this.returnZone?.destroy();
    this.poweredGate = undefined;
    this.poweredTitle = undefined;
    this.poweredEquation = undefined;
    this.poweredReplayHint = undefined;
    this.returnIcon = undefined;
    this.returnLabel = undefined;
    this.returnZone = undefined;
    this.createChallenge();
    this.transitioning = false;
  };

  private readonly enterNumberForge = () => {
    if (this.phase !== 'powered' || this.transitioning || !canEnterNumberForge(this.textures)) return;
    this.transitioning = true;
    audio.portalActivate();
    discoverNumberForge();
    this.scene.start(NUMBER_FORGE_SCENE_KEY);
  };

  private readonly returnToCrossroads = () => {
    if (this.transitioning) return;
    this.transitioning = true;
    visitNumberKingdomDistrict(NUMBER_KINGDOM_DISTRICTS.kingdomCrossroads);
    this.scene.start('KingdomCrossroads');
  };

  private readonly returnHome = () => {
    if (this.transitioning) return;
    this.transitioning = true;
    SaveManager.setLastScene(ADDITION_CITADEL_SCENE_KEY);
    SaveManager.save();
    this.scene.start('HomeHub');
  };

  private readonly handleResize = () => {
    if (this.resizeTimer !== undefined) window.clearTimeout(this.resizeTimer);
    this.resizeTimer = window.setTimeout(() => {
      if (!this.scene.isActive()) return;
      configureNumberKingdomDistrictRenderer(this);
      this.layout();
    }, 80);
  };

  private layout() {
    const { safe, compact } = getNumberKingdomDistrictLayout(this);
    this.backLabel.setPosition(safe.x + (compact ? 182 : 206), safe.y + 12)
      .setFontSize(compact ? 16 : 18);
    this.homeButton.layout(safe, compact, 'left');
    if (this.phase === 'arrival') {
      const arrivalPanelWidth = Math.min(safe.width * 0.78, 850);
      const arrivalPanelHeight = compact ? 116 : 132;
      this.arrivalPanel?.setPosition(safe.centerX, safe.y + (compact ? 72 : 92))
        .setScale(arrivalPanelWidth / 750, arrivalPanelHeight / 215);
      this.arrivalTitle?.setPosition(safe.centerX, safe.y + (compact ? 45 : 56))
        .setFontSize(compact ? 29 : 35);
      this.arrivalPrompt?.setPosition(safe.centerX, safe.y + (compact ? 95 : 111))
        .setFontSize(compact ? 16 : 19)
        .setWordWrapWidth(Math.min(safe.width * 0.68, 720));
      this.arrivalCosmo?.setPosition(safe.x + (compact ? 100 : 125), safe.bottom - 100)
        .setDisplaySize(compact ? 150 : 185, compact ? 134 : 165);
      this.enterIcon?.setPosition(safe.centerX + 115, safe.bottom - (compact ? 72 : 88));
      this.enterZone?.setPosition(safe.centerX, safe.centerY + 45);
    } else if (this.phase === 'challenge') {
      this.combineView?.layout(safe, compact);
    } else {
      const gateWidth = compact ? 470 : 560;
      const gateHeight = compact ? 430 : 510;
      this.poweredGate?.setPosition(safe.centerX, safe.centerY + (compact ? 45 : 55))
        .setScale(gateWidth / 970, gateHeight / 890);
      this.poweredTitle?.setPosition(safe.centerX, safe.y + (compact ? 52 : 68))
        .setFontSize(compact ? 30 : 38);
      this.poweredEquation?.setPosition(safe.centerX, safe.y + (compact ? 102 : 124))
        .setFontSize(compact ? 26 : 32);
      this.poweredReplayHint?.setPosition(safe.centerX, safe.y + (compact ? 139 : 166))
        .setFontSize(compact ? 14 : 17);
      const returnY = safe.bottom - 44;
      const returnX = safe.centerX + (compact ? 110 : 140);
      this.returnIcon?.setPosition(returnX + 85, returnY).setScale(
        (compact ? 68 : 82) / 590,
        (compact ? 58 : 70) / 430,
      );
      this.returnLabel?.setPosition(returnX + 38, returnY).setFontSize(compact ? 17 : 20);
      this.returnZone?.setPosition(returnX, returnY);
    }
  }

  private shutdown() {
    SaveManager.save();
    this.unsubscribeLifecycle?.();
    this.unsubscribeLifecycle = undefined;
    audio.leaveSoundscape({ id: 'number-kingdom' });
    window.removeEventListener('resize', this.handleResize);
    if (this.resizeTimer !== undefined) window.clearTimeout(this.resizeTimer);
    this.input.keyboard?.off('keydown-ESC', this.returnToCrossroads, this);
    this.input.keyboard?.off('keydown-M', this.returnHome, this);
    this.input.keyboard?.off('keydown-ENTER', this.startChallenge, this);
    this.input.keyboard?.off('keydown-SPACE', this.startChallenge, this);
    this.input.keyboard?.off('keydown-ENTER', this.returnToCrossroads, this);
    this.input.keyboard?.off('keydown-SPACE', this.returnToCrossroads, this);
    this.input.keyboard?.off('keydown-ENTER', this.enterNumberForge, this);
    this.input.keyboard?.off('keydown-SPACE', this.enterNumberForge, this);
    this.input.keyboard?.off('keydown-R', this.replayChallenge, this);
    this.combineView?.destroy();
    this.combineView = undefined;
    this.tweens.killAll();
  }
}
